import { useMemo } from "react";
import { useSelector } from "react-redux";

import type { RootState } from "../../../store/store";
import { canWriteFinanceiroFromToken } from "../../../utils/authToken";
import { DP_SOURCES, ENTRY_SOURCE_LABELS } from "../api/finTypes";
import type { FinCategory, FinCategoryKind, FinEntrySource } from "../api/finTypes";

/**
 * Quem escreve no Financeiro: ADMIN e FINANCEIRO (principal ou adicional).
 * FINANCEIRO_LEITURA vê as mesmas telas sem nenhum botão de escrita — o backend
 * recusa de qualquer jeito; esconder é para não oferecer o que dá 403.
 */
export function useFinPermissions() {
  const token = useSelector((s: RootState) => s.auth.accessToken);
  return useMemo(() => ({ token: token ?? "", canWrite: canWriteFinanceiroFromToken(token) }), [token]);
}

const pad2 = (n: number) => String(n).padStart(2, "0");

/** Data local como 'YYYY-MM-DD' (o que o `<input type="date">` usa). */
export const toYmd = (date: Date) => `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;

export const todayYmd = () => toYmd(new Date());

/** Primeiro e último dia do mês de uma data local. */
export const monthRange = (base = new Date(), offsetMonths = 0) => {
  const first = new Date(base.getFullYear(), base.getMonth() + offsetMonths, 1);
  const last = new Date(base.getFullYear(), base.getMonth() + offsetMonths + 1, 0);
  return { from: toYmd(first), to: toYmd(last) };
};

/** 'YYYY-MM' → 'out/2026'; 'YYYY-MM-DD' → '05/10'. */
export const periodLabel = (period: string) => {
  const [y, m, d] = period.split("-");
  if (d) return `${d}/${m}`;
  const months = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
  return `${months[Number(m) - 1]}/${y}`;
};

/** Valor digitado em reais ("1.234,56", "1234.56") → número, ou NaN. */
export const parseMoneyInput = (value: string): number => {
  const raw = value.trim();
  if (!raw) return NaN;
  const normalized = raw.includes(",") ? raw.replace(/\./g, "").replace(",", ".") : raw;
  const n = Number(normalized);
  return Number.isFinite(n) ? Math.round(n * 100) / 100 : NaN;
};

export type CategoryOption = { id: string; label: string; isGroup: boolean; isActive: boolean };

/** Árvore → lista para `<select>`: "Pessoal › Salários", com o grupo antes das filhas. */
export function flattenCategories(tree: FinCategory[], kind?: FinCategoryKind): CategoryOption[] {
  const out: CategoryOption[] = [];
  const walk = (nodes: FinCategory[], path: string[]) => {
    for (const node of nodes) {
      if (kind && node.kind !== kind) continue;
      const label = [...path, node.name].join(" › ");
      out.push({ id: node.id, label, isGroup: node.children.length > 0, isActive: node.isActive });
      walk(node.children, [...path, node.name]);
    }
  };
  walk(tree, []);
  return out;
}

/**
 * Parcelas como o backend divide: as primeiras arredondadas para baixo, a última
 * com o resto — a prévia do formulário bate centavo a centavo com o que é salvo.
 */
export function previewInstallments(total: number, count: number): number[] {
  if (!Number.isFinite(total) || total <= 0 || count < 1) return [];
  const cents = Math.round(total * 100);
  const base = Math.floor(cents / count);
  return Array.from({ length: count }, (_, i) => (i === count - 1 ? cents - base * (count - 1) : base) / 100);
}

/** CPF (11) ou CNPJ (14) formatado; outro tamanho volta como veio. */
export const formatDocument = (doc?: string | null) => {
  if (!doc) return "—";
  if (doc.length === 11) return doc.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  if (doc.length === 14) return doc.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
  return doc;
};

/** Origens no filtro (`?origem=`): "dp" junta todas as pontes do DP (ADR 0029). */
export const ENTRY_ORIGINS: Record<string, { label: string; sources: FinEntrySource[] }> = {
  // A provisão (só competência) não é conta a pagar: fica fora do "Pessoal (DP)" e só aparece pedida pela origem.
  dp: { label: "Pessoal (DP)", sources: DP_SOURCES.filter((s) => s !== "PROVISAO") },
  ...Object.fromEntries(
    (Object.keys(ENTRY_SOURCE_LABELS) as FinEntrySource[]).map((src) => [
      src.toLowerCase(),
      { label: ENTRY_SOURCE_LABELS[src], sources: [src] },
    ])
  ),
};
